import { type FC, useEffect, useState } from "react";

import { userIsInquirer, userIsLoggedIn } from "../utils/roles";

import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import useMediaQuery from "../common/hooks/useMediaQuery";
import { Col, Row } from "../common/components/Layout";

import { IconType } from "../common/components/icon/IconType";
import { EmailsOrderListTable } from "./components/EmailsOrderListTable";

import { deleteAccount, getList, saveAccount } from "./api/broker";
import { storageEngine, TYPE } from "../utils/storage";
import { type AccountInfoData } from "../AccountSelection/AccountSelectionTabelView/api/GetAccountList";
import Spinner from "../common/components/Spinner/Spinner";

import { type EmailOrderQuoteData } from "./types";
import { useEmailOrderQuotePopup } from "./components/EmailOrderQuotePopup";
import { userCanSeeMyCompanySettings } from "../AccountSettings/helper/permissions";
import { redirectToUrl } from "../utils/url/urlUtils";
import { HOMEPAGE } from "../utils/url/staticUrls";
import { useEmailOrderQuoteConfirmation } from "./components/EmailOrderQuotePopupConfirmation";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "../utils/screenSizes";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { Alert } from "~/common/m3/Alert/Alert";

export const OrderQuoteSettingsPage: FC = () => {
  // guard component
  if (!userCanSeeMyCompanySettings || !userIsLoggedIn()) {
    redirectToUrl(HOMEPAGE);
    return null;
  }
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  // grab from session storage the accounts list
  let userAccountsString = storageEngine(TYPE.SESSION).getItem("userAccounts") || "";
  if ("" === userAccountsString) {
    userAccountsString = "[]";
  }
  const userAccountsData: AccountInfoData[] = JSON.parse(userAccountsString) as AccountInfoData[];
  const isInquirer = userIsInquirer();

  // internal states
  const [emailAccounts, setEmailAccounts] = useState<EmailOrderQuoteData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  const getEmailList = () => {
    setIsLoading(true);
    getList()
      .then((data) => data.filter((account) => account.recipientEmail || account.recipientEmail.trim() !== "")) // filter out accounts that doesn't have proper emails set
      .then(setEmailAccounts)
      .then(() => setHasError(false))
      .catch((error) => {
        setHasError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // on init
  useEffect(getEmailList, []);

  const onRowUpdate = (data: EmailOrderQuoteData) => {
    // live update displayed account list without server side request
    const index = emailAccounts.findIndex((account) => account.recipientEmail === data.recipientEmail);
    if (index > -1) {
      const newList = [...emailAccounts];
      newList[index] = data;
      setEmailAccounts(newList);
    }
    saveAccount(data);
  };

  const onRowDelete = (data: EmailOrderQuoteData) => openConfirmation(data);

  const requestServerRowDelete = (data: EmailOrderQuoteData) => {
    // live update displaied account list without server side request
    const index = emailAccounts.findIndex((account) => account.recipientEmail === data.recipientEmail);
    if (index > -1) {
      const newList = [...emailAccounts];
      newList.splice(index, 1);
      setEmailAccounts(newList);
    }
    deleteAccount(data);
    // close modal if opened and user confirmed for deletion
    modal && close();
  };

  const onSubmit = (data: EmailOrderQuoteData) => saveAccount(data).finally(getEmailList);

  const { modal, open, close } = useEmailOrderQuotePopup({
    availableAccounts: userAccountsData,
    onSubmit,
    onDelete: onRowDelete,
  });

  const {
    modal: modalConfirmation,
    open: openConfirmation,
    visible: modalConfirmationVisible,
  } = useEmailOrderQuoteConfirmation({
    onOk: requestServerRowDelete,
    onCancel: (_) => {},
  });

  const content = isLoading ? (
    <Spinner />
  ) : hasError ? (
    <Alert severity={"error"}>{t("page.orderQuoteSettings.status.error")}</Alert>
  ) : emailAccounts.length > 0 ? (
    <EmailsOrderListTable
      data={emailAccounts}
      availableAccounts={userAccountsData}
      onRowUpdate={onRowUpdate}
      onRowDelete={onRowDelete}
      onRowEdit={open}
    />
  ) : (
    <Alert severity={"info"}>{t("page.orderQuoteSettings.status.noResults")}</Alert>
  );

  return (
    <div className={"scha__order-quote-settings " + (isDesktop ? "desktop" : "mobile")}>
      <PageTitle title={t(`page.${isInquirer ? "quoteSettings" : "orderQuoteSettings"}.title`)} />
      <Row className={"scha__order-quote-settings__header"}>
        <Col size={9} className={"scha__order-quote-settings__header__subtitle"}>
          <h3 className="scha__order-quote-settings__section__title headline-small m-0">
            {t(`page.${isInquirer ? "quoteSettings" : "orderQuoteSettings"}.subtitle`)}
          </h3>
        </Col>
        <Col size={3} className={"scha__order-quote-settings__header__add-new-button"}>
          <Button
            data-test-id={"scha__order-quote-settings-btn-add-new"}
            startIcon={<DefaultButtonIcon type={IconType.Plus} />}
            onClick={() => open()}
          >
            {t("page.orderQuoteSettings.addNewEmail")}
          </Button>
        </Col>
      </Row>
      <div className="scha__order-quote-settings__main">{content}</div>
      {!modalConfirmationVisible && modal}
      {modalConfirmation}
    </div>
  );
};
